import * as p from '@clack/prompts'
import pc from 'picocolors'

import { ensureVercelAuth } from './auth.js'
import { provisionNeon, provisionNeonInteractive } from './neon.js'
import { createVercelProject } from './project.js'
import { resolveVercelRunner } from './runner.js'
import type {
  AuthFailureReason,
  NeonFailureReason,
  NeonProvisionResult,
  VercelNeonFlowResult,
  VercelRunner
} from './types.js'

/**
 * Top-level orchestrator for the Vercel-backed Neon flow during `betterstart
 * init`: resolve a runner → ensure auth (interactive login link) → create + link
 * a Vercel project → provision Neon and capture the DATABASE_URL.
 *
 * When interactive, provisioning runs attached to the terminal so the user can
 * accept marketplace terms and pick the Free plan in Vercel's own prompts — the
 * non-interactive (`--format json`) path is reserved for `--yes` + token.
 *
 * Never throws. Any failure at any step resolves to `{ ok: false }` so the
 * caller can fall back to manual connection-string entry.
 */
export async function runVercelNeonFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  plan?: string
  env?: NodeJS.ProcessEnv
}): Promise<VercelNeonFlowResult> {
  const env = options.env ?? process.env

  try {
    const runner = await resolveVercelRunner()

    // Auth may print an interactive login link — keep it outside any spinner.
    const auth = await ensureVercelAuth(runner, options.cwd, { env })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }
    if (auth.username) {
      p.log.success(`Signed in to Vercel as ${pc.cyan(auth.username)}`)
    }

    // Create + link the Vercel project (non-interactive; safe under a spinner).
    const projectSpinner = p.spinner()
    projectSpinner.start(`Creating Vercel project ${pc.cyan(options.projectName)}`)
    const project = await createVercelProject(runner, options.cwd, options.projectName, env)
    projectSpinner.stop(
      project.linked
        ? `Linked Vercel project ${pc.cyan(project.name)}`
        : `Using Vercel project ${pc.cyan(project.name)}`
    )

    const neon = await provisionNeonForMode(runner, options)
    if (neon.failure || !neon.databaseUrl) {
      p.log.warn(neonFailureMessage(neon.failure))
      if (neon.detail) p.log.message(pc.dim(neon.detail))
      return { ok: false }
    }

    p.log.success(`Provisioned Neon Postgres for ${pc.cyan(project.name)}`)
    return {
      ok: true,
      databaseUrl: neon.databaseUrl,
      vercelProjectId: project.projectId,
      neonResourceName: neon.resourceName
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  }
}

function provisionNeonForMode(
  runner: VercelRunner,
  options: { cwd: string; interactive: boolean; plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const opts = { plan: options.plan, env: options.env }
  if (options.interactive) {
    p.log.info(
      `Create your Neon database in the Vercel prompts below ${pc.dim('(the Free plan is recommended).')}`
    )
    return provisionNeonInteractive(runner, options.cwd, opts)
  }
  return provisionNeon(runner, options.cwd, opts)
}

function authFailureMessage(reason?: AuthFailureReason): string {
  switch (reason) {
    case 'login-cancelled':
      return 'Vercel sign-in was cancelled.'
    case 'login-failed':
      return 'Could not verify your Vercel sign-in.'
    case 'timeout':
      return 'Vercel sign-in timed out.'
    case 'no-runner':
      return 'The Vercel CLI is not available.'
    default:
      return 'Could not sign in to Vercel.'
  }
}

function neonFailureMessage(reason?: NeonFailureReason): string {
  switch (reason) {
    case 'terms-required':
      return 'Provisioning needs the Neon marketplace terms accepted — falling back to manual entry.'
    case 'claim-required':
      return 'The Neon resource needs to be claimed — falling back to manual entry.'
    case 'env-pull-empty':
      return 'Provisioned Neon, but no DATABASE_URL came back — falling back to manual entry.'
    case 'timeout':
      return 'Neon provisioning timed out — falling back to manual entry.'
    default:
      return 'Neon provisioning did not complete — falling back to manual entry.'
  }
}
